import { adaptRunDetail, adaptRunHistory } from '../service/adaptRun'
import { classifyInsightActionError, isCanceled } from '../service/classifyInsightActionError'
import { previewKey, type InsightActionStore } from '../stores/insightActionStore'

import type { InsightActionApiSlice, InsightActionScope, RunScope } from './insightActionFlow.types'

export const HISTORY_PAGE_SIZE = 50

export interface LoadHistoryFlowDeps {
  api: InsightActionApiSlice<'runHistory'>
  store: InsightActionStore
}

export class LoadHistoryFlow {
  constructor(private readonly deps: LoadHistoryFlowDeps) {}

  async execute(scope: InsightActionScope, signal?: AbortSignal): Promise<void> {
    const store = this.deps.store
    if (store.historyLoading) return
    store.setHistoryLoading(true)

    const key = previewKey(scope.accountId, scope.workspaceId, scope.insightId)
    try {
      const page = adaptRunHistory(
        await this.deps.api.runHistory(
          scope.accountId,
          scope.workspaceId,
          scope.insightId,
          { limit: HISTORY_PAGE_SIZE },
          signal,
        ),
      )
      store.setHistory(key, page.runs, page.total)
    } catch (cause) {
      if (isCanceled(classifyInsightActionError(cause))) {
        store.setHistoryLoading(false)
        return
      }
      store.setHistory(key, [], 0)
    }
  }
}

export interface LoadRunDetailFlowDeps {
  api: InsightActionApiSlice<'runDetail'>
  store: InsightActionStore
}

export class LoadRunDetailFlow {
  constructor(private readonly deps: LoadRunDetailFlowDeps) {}

  async execute(
    scope: RunScope,
    runId: string,
    options: { force?: boolean; signal?: AbortSignal } = {},
  ): Promise<void> {
    const store = this.deps.store
    if (store.runDetails[runId] && options.force !== true) return
    try {
      const detail = adaptRunDetail(
        await this.deps.api.runDetail(scope.accountId, scope.workspaceId, runId, options.signal),
      )
      store.setRunDetail(runId, detail)
    } catch {
      return
    }
  }
}
